import React, { useState } from 'react';
import type { Book } from '../../../shared/types/index.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { CertificateModal } from '../../uibn/components/CertificateModal.js';
import { EditionTreeModal } from '../../editions/components/EditionTreeModal.js';
import { EmbedBadgeModal } from '../../badges/components/EmbedBadgeModal.js';
import { BookLabelPrintModal } from './BookLabelPrintModal.js';
import { canEditBookMetadata } from './book-list/bookListUtils.js';
import {
  BookExpandableHeader,
  BookBarcodeQrSection,
  BookMetadataGrid,
  BookAuditTrailSection,
  BookExpandableActions,
} from './book-expandable-detail/index.js';

interface BookExpandableDetailProps {
  book: Book;
  onOpenModal: (book: Book) => void;
  onEdit?: (book: Book) => void;
  onCollapse?: () => void;
}

export const BookExpandableDetail: React.FC<BookExpandableDetailProps> = ({
  book,
  onOpenModal,
  onEdit,
  onCollapse,
}) => {
  const { user } = useAuthStore();
  const [activeCodeTab, setActiveCodeTab] = useState<'barcode' | 'qr'>('barcode');
  const [copiedUibn, setCopiedUibn] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [isCertOpen, setIsCertOpen] = useState(false);
  const [isEditionTreeOpen, setIsEditionTreeOpen] = useState(false);
  const [isEmbedBadgeOpen, setIsEmbedBadgeOpen] = useState(false);
  const [isPrintLabelOpen, setIsPrintLabelOpen] = useState(false);

  const copyUibn = () => {
    navigator.clipboard.writeText(book.uibn);
    setCopiedUibn(true);
    setTimeout(() => setCopiedUibn(false), 2000);
  };

  const copyRecordId = () => {
    navigator.clipboard.writeText(book.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const verificationUrl = `${window.location.origin}/#search=${encodeURIComponent(book.uibn)}`;
  const copyVerificationUrl = () => {
    navigator.clipboard.writeText(verificationUrl);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const isAuthorOwner = Boolean(
    user?.role === 'penulis' && (book.authorId === user.id || book.authorName.toLowerCase() === user.name.toLowerCase())
  );
  const isPublisherOwner = Boolean(
    user?.role === 'penerbit' && (
      book.publisherId === user.id ||
      (user.organization && book.publisherName.toLowerCase() === user.organization.toLowerCase()) ||
      book.publisherName.toLowerCase() === user.name.toLowerCase() ||
      (user.publisherCode && book.uibn.includes(user.publisherCode))
    )
  );
  const isAdmin = user?.role === 'admin';
  const canEdit = canEditBookMetadata(book, user);

  return (
    <div
      id={`expandable-detail-${book.id}`}
      className="mt-3.5 pt-3.5 border-t border-indigo-100 bg-slate-50/90 -mx-4 -mb-4 sm:-mx-5 sm:-mb-5 p-4 sm:p-5 rounded-b-2xl space-y-4 text-xs animate-in fade-in slide-in-from-top-2 duration-200"
    >
      {/* Header */}
      <BookExpandableHeader bookId={book.id} onCollapse={onCollapse} />

      {/* 1. Barcode & QR Code Status Section */}
      <BookBarcodeQrSection
        book={book}
        activeCodeTab={activeCodeTab}
        canEdit={canEdit}
        onSelectTab={setActiveCodeTab}
      />

      {/* 2. Detailed Metadata Grid */}
      <BookMetadataGrid book={book} />

      {/* 3. Registration Timestamp & Audit Trail */}
      <BookAuditTrailSection
        book={book}
        copiedId={copiedId}
        copiedUrl={copiedUrl}
        onCopyRecordId={copyRecordId}
        onCopyVerificationUrl={copyVerificationUrl}
      />

      {/* 4. Responsive Action Bar */}
      <BookExpandableActions
        book={book}
        canEdit={canEdit}
        userRole={user?.role}
        copiedUibn={copiedUibn}
        onCopyUibn={copyUibn}
        onEdit={onEdit}
        onOpenCert={() => setIsCertOpen(true)}
        onOpenPrintLabel={() => setIsPrintLabelOpen(true)}
        onOpenEditionTree={() => setIsEditionTreeOpen(true)}
        onOpenEmbedBadge={() => setIsEmbedBadgeOpen(true)}
        onOpenModal={onOpenModal}
      />

      {/* Certificate Modal */}
      <CertificateModal
        book={book}
        isOpen={isCertOpen}
        onClose={() => setIsCertOpen(false)}
      />

      {/* Silsilah Edisi Modal */}
      <EditionTreeModal
        book={book}
        isOpen={isEditionTreeOpen}
        onClose={() => setIsEditionTreeOpen(false)}
        onSelectBook={(selected) => onOpenModal(selected)}
      />

      {/* Embed Badge Generator Modal */}
      <EmbedBadgeModal
        book={book}
        isOpen={isEmbedBadgeOpen}
        onClose={() => setIsEmbedBadgeOpen(false)}
      />

      {/* Print Labels Modal */}
      <BookLabelPrintModal
        book={book}
        isOpen={isPrintLabelOpen}
        onClose={() => setIsPrintLabelOpen(false)}
      />
    </div>
  );
};
